import React, { useState } from "react";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import { FiSearch } from "react-icons/fi";

// Import your tab components
import MyInventory from "./TabContent/InventoryTab";
import AnalyticsInventory from "./TabContent/AnalyticsTab";
import RestockAlerts from "./TabContent/RestockAlerts";
import TemplatesInventory from "./TabContent/TemplateTab";
import DraftInventory from "./TabContent/DraftTab";
import NewInventory from "./TabContent/NewInventoryTab";

import { useSelector, useDispatch } from "react-redux";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import {
  addTab,
  removeTab,
  setActiveTab,
} from "../../../redux/slices/inventorySlice";
import InventorySearchSubTabs from "./TabContent/InventorySearchSubTabs";

const moduleName = "inventory";

const InventoryDetails = () => {
  const dispatch = useDispatch();
  const moduleTabs =
    useSelector((state) => selectModuleTabs(moduleName)(state)) || {};
  const { tabs = [], activeTab = 0, defaultTabsCount = 0 } = moduleTabs;

  // ✅ Manage search query & filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchTrigger, setSearchTrigger] = useState(false); // ✅ Force fetch when search button is clicked
  const [totalItems, setTotalItems] = useState(0); // ✅ Track total items

  // ✅ Handle search button click
  // const handleSearch = () => {
  //   setSearchTrigger((prev) => !prev); // Toggle state to trigger API call
  // };

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    // Create a unique id for the search tab and provide custom name/content.
    const newTabId = `search-${Date.now()}`;
    dispatch(
      addTab({
        id: newTabId,
        name: `Search: ${searchQuery}`,
        // Optionally, add a component or other identifier for the UI to render search results.
        // For example, you might add a field like `contentType: 'searchResults'` or even a component.
        contentType: "searchResults",
        searchQuery,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
    // dispatch(setActiveTab({ index: state.defaultTabs.length + state.dynamicTabs.length })); // Adjust as needed
  };

  // ✅ Handle status filter change
  const handleStatusFilter = (selectedValue) => {
    if (selectedValue === "All Statuses") {
      setStatusFilter(null); // Show all statuses
    } else if (selectedValue === "Active") {
      setStatusFilter(true); // Send boolean `true`
    } else if (selectedValue === "Inactive") {
      setStatusFilter(false); // Send boolean `false`
    }
  };

  // ✅ Clear filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setStatusFilter(null);
    setSearchTrigger((prev) => !prev); // ✅ Force fetch with cleared filters
  };

  // Default tab contents
  const defaultTabContents = {
    "my-inventory": (
      <MyInventory
        key="my-inventory"
        searchQuery=""
        statusFilter={statusFilter}
        searchTrigger={searchTrigger}
        setTotalItems={setTotalItems}
        totalItems={totalItems}
      />
    ),
    "analytics-inventory": <AnalyticsInventory key="analytics-inventory" />,
    "restock-alerts": <RestockAlerts key="restock-alerts" />,
    "templates-inventory": <TemplatesInventory key="templates-inventory" />,
    "drafts-inventory": <DraftInventory key="drafts-inventory" />,
  };

  // Add a new tab
  // const handleAddTab = () => {
  //   dispatch(addTab({ module: moduleName }));
  // };

  const handleAddTab = () => {
    dispatch(addTab());
  };
  

  // Remove a tab (only dynamic tabs can be removed)
  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) {
      alert("Default tabs cannot be removed.");
      return;
    }
    dispatch(removeTab({ module: moduleName, tabIndex: index }));
  };

  return (
<div className="p-fluid-1 shadow-md flex flex-col flex-1 h-full min-h-0 overflow-hidden">
    {/* Top Action/Filter Bar */}
    <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
      {/* Left side: Filter buttons, search box */}
      <div className="flex flex-wrap items-center gap-2">
          {/* ✅ Status Dropdown Filter */}
          <FilterButton
            text={
              statusFilter === null
                ? "All Statuses"
                : statusFilter
                ? "Active"
                : "Inactive"
            } // Display correct text
            options={["All Statuses", "Active", "Inactive"]}
            selectedValue={
              statusFilter === null
                ? "All Statuses"
                : statusFilter
                ? "Active"
                : "Inactive"
            }
            onSelect={handleStatusFilter}
          />

          {/* ✅ Clear Filters Button */}
          <FilterButton
            text="Clear Filters"
            backgroundColor="gray"
            textColor="black"
            onClick={handleClearFilters}
          />

          {/* ✅ Search Box + Total items Count */}
          <div className="relative flex items-center border border-gray-300 bg-white rounded-full px-3 py-1.5 w-80">
            <input
              type="text"
              placeholder="Search inventory..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              className="w-full outline-none text-fluid-sm text-gray-800"
            />
            <button
              onClick={handleSearch}
              className="absolute right-3 text-gray-500 hover:text-gray-700"
            >
              <FiSearch size={18} />
            </button>
          </div>

          {/* ✅ Total items Display (Next to Search Box) */}
          <span className="ml-3 text-gray-500 text-sm">
            {totalItems} Items
          </span>
        </div>

        {/* 🔹 Actions */}
        <div className="flex items-center gap-2">
        <div className="p-2 rounded-md hover:bg-gray-100 transition cursor-pointer">
        </div>
  
        <Button
            text="Add New Item"
            onClick={handleAddTab}
            backgroundColor="#223688"
            textColor="white"
            fontSize="14px"
            padding="10px 18px"
            borderRadius="24px"
          />
        </div>
      </div>

      {/* Tabs Component */}
      <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
      <Tabs
          key={tabs.length}
          tabs={tabs.map((tab) => tab.name)}
          tabContents={tabs.map((tab) => {
            if (tab.contentType === "searchResults") {
              return (
                <InventorySearchSubTabs
                    key={tab.id}
                    searchQuery={tab.searchQuery}
                    searchTrigger={searchTrigger}
                    setTotalItems={setTotalItems}
                    totalItems={totalItems}
                    statusFilter={statusFilter}
              />
              );
            }
            return (
              defaultTabContents[tab.id] || (
                <NewInventory
                  key={tab.id}
                  title={tab.name}
                  tabId={tab.id}
                  isDynamic={tab.isDynamic}
                />
              )
            );
          })}
          activeTab={activeTab}
          onTabChange={(index) => dispatch(setActiveTab({ index }))}
          isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}
        />
        {/* <Tabs
          moduleName={moduleName}
          tabs={tabs.map((tab) => tab.name)}
          tabContents={tabs.map(
            (tab) => 
            {
              if (tab.contentType === "searchResults") {
                return (
                  <InventorySearchSubTabs
                    key={tab.id}
                    searchQuery={tab.searchQuery}
                    searchTrigger={searchTrigger}
                    setTotalItems={setTotalItems}
                    totalItems={totalItems}
                    statusFilter={statusFilter}
                  />
                );
              }
              return(
              defaultTabContents[tab.id] || (
                <NewInventory
                  key={tab.id}
                  title={tab.name}
                  tabId={tab.id}
                  isDynamic={tab.isDynamic}
                />
                )
              );
            
            })}
          activeTab={activeTab}
          onTabChange={(index) =>
            dispatch(setActiveTab({ module: moduleName, index }))
          }
          isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
          onRemoveTab={handleRemoveTab}
          aria-label="Inventory tabs"
        /> */}
      </div>
    </div>
  );
};

export default InventoryDetails;
